import { Component, effect, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Table, TableModule } from 'primeng/table';
import { TableComponent } from '../../../../core/components/table/table.component';
import { TableReadOnlyComponent } from '../../../../core/components/table-read-only/table-read-only.component';
import { ButtonModule } from 'primeng/button';
import { AddCedComponent } from './add-ced/add-ced.component';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { Ced } from './ced.model';
import { ReferentialsCedService } from './referentials-ced.service';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StoreService } from '../../../../core/services/store.service';
import { GenericErrorResponse } from '../../../../core/models/generic-response.model';
import { HttpErrorResponse } from '@angular/common/http';
import { InlineResponse } from '../../../../core/models/inline-response.model';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FilterEventService } from "../../../../core/services/filter-event.service";

const dialogConfig = {
  modal: true,
  width: '30vw',
  height: 'auto',
  maximizable: true,
  breakpoints: {
    '1199px': '60vw', // Largeur à 60% du viewport si la fenêtre fait moins de 1199px de large
    '991px': '70vw', // Largeur à 70% du viewport si la fenêtre fait moins de 991px
    '575px': '90vw', // Largeur à 90% du viewport si la fenêtre fait moins de 575px
  },
};

export enum NiveauCED {
  CATEGORIE,
  SOUS_CATEGORIE,
  DECHET,
  CATEGORY,
  SUB_CATEGORY,
  WASTE,
  CATEGORIA,
  SUBCATEGORIA,
  RESIDUO,
}

@Component({
  selector: 'app-referentials-ced',
  standalone: true,
  imports: [CommonModule, TableModule, TableComponent, TableReadOnlyComponent, ButtonModule, InputTextModule, CheckboxModule, ReactiveFormsModule, AddCedComponent, FormsModule, ToastModule, DropdownModule, TooltipModule, TranslocoModule],
  providers: [DialogService, MessageService, provideTranslocoScope({ scope: 'referentiels' })],
  templateUrl: './referentials-ced.component.html',
  styleUrl: './referentials-ced.component.css',
})
export class ReferentialsCedComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private filterSubscription!: Subscription;
  @ViewChild('cedTable') table!: Table;

  userHasReadWritePermission: boolean = false;

  ref: DynamicDialogRef | undefined;

  cedResult!: Ced[];
  selectedCed?: Ced;
  totalRecords: number = 0;

  disableSubmit = false;
  errors: GenericErrorResponse[] = [];
  loading: boolean = false;

  editRowIndex: number | null = null; // Variable pour garder la ligne en cours d'édition
  editingLine: any = null;
  clonedCed: { [s: string]: Ced } = {};

  niveauCedOptions = [
    { label: 'CATEGORIE', value: 'CATEGORIE' },
    { label: 'SOUS_CATEGORIE', value: 'SOUS_CATEGORIE' },
    { label: 'DECHET', value: 'DECHET' },
  ];

  cedForm = this.formBuilder.group({
    id: this.formBuilder.control<number | null>(null),
    code: [''],
    danger: this.formBuilder.control<boolean | false>(false),
    libelle: [''],
    niveauCED: [''],
    displayCode: [''],
  });

  constructor(
    private cedService: ReferentialsCedService,
    private storeService: StoreService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private formBuilder: FormBuilder,
    private translocoService: TranslocoService,
    private filterEventService: FilterEventService,
  ) {
    effect(() => {
      const selectedContrat = this.storeService.selectedContract();
      this.ngOnInit();
    });
  }

  ngOnInit(): void {
    this.userHasReadWritePermission = this.storeService.hasAuthority('PARAMETRES_REFERENTIEL_RW');
    this.loadCed();
    this.filterSubscription = this.filterEventService.filterEvent$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event === 'reset') {
        this.clearFilter();
      }
    });
  }

  private clearFilter() {
    this.table.clear();
  }

  private loadCed() {
    this.loading = true;
    this.cedService
      .getCedData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.cedResult = data;
        this.totalRecords = this.cedResult.length;
        this.loading = false;
      });
  }

  ajouterCed() {
    this.ref = this.dialogService.open(AddCedComponent, {
      ...dialogConfig,
      header: this.translocoService.translate('jgrid.nav.addtitle'),
    });
    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.showSuccessMessage('La ligne a été ajouté avec succès.');
        this.loadCed();
      }
    });
  }

  modifierCed(ced: Ced, index: number) {
    this.editRowIndex = index;
    this.editingLine = ced;
    this.selectedCed = ced;
    const id = ced.id;
    this.cedForm.controls.code.patchValue(ced.code);
    this.cedForm.controls.id.patchValue(ced.id);
    this.cedForm.controls.libelle.patchValue(ced.libelle);
    this.cedForm.controls.danger.patchValue(ced.danger);
    this.cedForm.controls.displayCode.patchValue(ced.displayCode);
    this.cedForm.controls.niveauCED.patchValue(ced.niveauCED);
    this.clonedCed[id.toString()] = { ...ced };
  }

  validerModifsCed(ced: Ced) {
    this.cedService.updateCedData(this.cedForm.value as Ced).subscribe({
      next: (response) => {
        this.handleInlineResponse(response);
      },
      error: (error) => {
        this.handleHttpErrorResponse(error);
      },
    });
  }

  annulerModifsCed(ced: Ced, index: number) {
    this.editRowIndex = null;
    this.editingLine = null;
    const id = ced.id;
    this.cedResult
      .filter((c) => c.id === id)
      .map((ced) => {
        ced = this.clonedCed[id.toString()];
      });
    delete this.clonedCed[id.toString()];
  }

  onRowSelect(ced: Ced) {
    this.selectedCed = ced;
  }

  private handleInlineResponse(response: InlineResponse): void {
    this.disableSubmit = false;
    if (response.success) {
      this.showSuccessMessage('La ligne a été modifiée avec succès.');
      this.loadCed();
    } else {
      this.errors = response.errors;
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('error.popup.titre.defaut'),
        detail: 'Une erreur est survenue : ' + this.errors.map((e) => e.errorMsg).join(', '),
        life: 3000,
      });
    }
  }

  private handleHttpErrorResponse(error: HttpErrorResponse) {
    this.disableSubmit = false;
    this.errors = [];
    this.errors.push({
      errorInputId: error.status.toString(),
      errorCode: error.status.toString(),
      errorMsg: error.statusText,
    } as GenericErrorResponse);
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
  }

  showInfoMessage(infoMessage: string) {
    this.messageService.add({ severity: 'info', summary: this.translocoService.translate('info.popup.titre.defaut'), detail: infoMessage, life: 3000 });
  }

  showSuccessMessage(successMessage: string) {
    this.messageService.add({ severity: 'success', summary: this.translocoService.translate('summary.success'), detail: successMessage, life: 3000 });
  }

  showErrorMessage(errorMessage: string) {
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: errorMessage, life: 3000 });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
